/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { Calendar } from '@zcode-ui/icons'
import { selectIsMounted } from '@zcode-ui/web-zumper-global-state'
import { PropertyFeatures } from '@zcode/enums'
import type { AnalyticsC, Origin } from '@zcode/nr-utils'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import cn from 'classnames'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getRequestedTour } from '../../../../../../actions/sendMessage/requestedTours'
import { TourButtonListCardContainer } from '../../../../../EqualCTAs/TourButton/TourButtonListCardContainer'
import css from './ListingCardTourButton.module.scss'

interface ListingCardTourButtonProps {
  analyticChannel:
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
  dataTestId?: string
  entity: Listable | Pin<RawPinInventorySearch>
  isSpotlightVariation?: boolean
  origin: Origin
  size?: string
}

const tourButtonContentFactory = (showWhiteIcon: boolean | undefined) => (isMessaged: boolean) => (
  <div className={cn(css.tourButtonContent, showWhiteIcon && css.tourButtonContentSpotlight)}>
    <Calendar />
    <p className={css.contentText}>{isMessaged ? 'Requested' : 'Tour'}</p>
  </div>
)

export const ListingCardTourButton = (props: ListingCardTourButtonProps) => {
  const { entity, isSpotlightVariation } = props
  const appIsMounted = useSelector(selectIsMounted)
  const tourButtonContent = useMemo(
    () => tourButtonContentFactory(isSpotlightVariation),
    [isSpotlightVariation]
  )

  if (
    entity.isExternal ||
    entity.features?.includes(PropertyFeatures.TOUR_DISABLED) ||
    !entity.isActive ||
    entity.waitlistEnabled
  ) {
    return null
  }
  const requestedTour = appIsMounted && getRequestedTour(entity)
  return (
    <TourButtonListCardContainer
      size={props.size ?? 'md'}
      buttonCustomStyle={isSpotlightVariation ? css.tourButtonSpotlight : css.tourButton}
      isMessaged={!!requestedTour}
      {...props}
    >
      {tourButtonContent}
    </TourButtonListCardContainer>
  )
}
